/**
 * Copyright 2022-present terrestris GmbH & Co. KG
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 * list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 * this list of conditions and the following disclaimer in the documentation
 * and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import React, {
  useEffect
} from 'react';

import {
  Divider
} from 'antd';

import OlControlMousePosition from 'ol/control/MousePosition';
import OlControlScaleLine from 'ol/control/ScaleLine';
import {
  createStringXY
} from 'ol/coordinate';

import {
  useTranslation
} from 'react-i18next';

import ScaleCombo from '@terrestris/react-geo/dist/Field/ScaleCombo/ScaleCombo';
import { useMap } from '@terrestris/react-util';

import './Footer.less';

export interface FooterProps extends React.ComponentProps<'div'> { }

export const Footer: React.FC<FooterProps> = ({
  ...restProps
}): React.JSX.Element => {
  const {
    t
  } = useTranslation();

  const map = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    const existingControl = map.getControls().getArray()
      .find(control => control instanceof OlControlScaleLine);

    if (existingControl) {
      return;
    }

    map.addControl(
      new OlControlScaleLine({
        target: 'scale-line-container'
      })
    );
  }, [map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const existingControl = map.getControls().getArray()
      .find(control => control instanceof OlControlMousePosition);

    if (existingControl) {
      return;
    }

    map.addControl(
      new OlControlMousePosition({
        coordinateFormat: createStringXY(2),
        projection: 'EPSG:3857',
        target: 'mouse-position'
      })
    );
  }, [map]);

  if (!map) {
    return <></>;
  }

  return (
    <div
      className="footer"
      {...restProps}
    >
      <div
        className="item-container left-items"
      >
        <div
          id="scale-line-container"
        />
        <Divider
          type="vertical"
        />
        <div
          className="scale-combo"
        >
          {t('Footer.scale')}:&nbsp;
          <ScaleCombo />
          <Divider
            type="vertical"
          />
        </div>
        <div
          className="reference-system"
        >
          {t('Footer.refSystem')}: {map.getView().getProjection().getCode()}
          <Divider
            type="vertical"
          />
        </div>
        <div
          className="mouse-position-wrapper"
        >
          {t('Footer.mousePosition')}:&nbsp;
          <div
            id="mouse-position"
            className="mouse-position"
          />
        </div>
      </div>
    </div>
  );
};

export default Footer;
